import {
  createRouter,
  createRootRoute,
  createRoute,
  redirect,
  Outlet,
} from '@tanstack/react-router';
import { AppLayout } from './layouts/AppLayout';
import { DashboardPage } from './pages/Dashboard';
import { ServiceOrdersPage } from './pages/ServiceOrders';
import { ServiceOrderDetailPage } from './pages/ServiceOrderDetail';
import { ServiceOrderNewPage } from './pages/ServiceOrderNew';
import { TeamPage } from './pages/Team';
import { guarded } from './components/RouteGuard';
import { FinancePage } from './pages/Finance';
import { InventoryPage } from './pages/Inventory';
import { SettingsPage } from './pages/Settings';
import { WisdomPage } from './pages/Wisdom';
import { LoginPage } from './pages/Login';
import { AppointmentsPage } from './pages/Appointments';
import { MechanicDetailPage } from './pages/MechanicDetail';
import { WorkshopSlotsPage } from './pages/WorkshopSlots';
import { CarsInShopPage } from './pages/CarsInShop';
import { ReportsPage } from './pages/Reports';
import { ToolReportsPage } from './pages/ToolReports';
import { SupportPage, SupportTicketPage } from './pages/Support';

const rootRoute = createRootRoute({
  component: Outlet,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
});

const layoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  component: AppLayout,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/dashboard' });
  },
});

const dashboardRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/dashboard',
  component: guarded('/dashboard', DashboardPage),
});

const serviceOrdersRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/service-orders',
  component: ServiceOrdersPage,
});

const serviceOrderNewRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/service-orders/new',
  component: guarded('/service-orders/new', ServiceOrderNewPage),
});

const serviceOrderDetailRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/service-orders/$osId',
  component: ServiceOrderDetailPage,
});

const teamRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/team',
  component: TeamPage,
});

const financeRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/finance',
  component: guarded('/finance', FinancePage),
});

const inventoryRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/inventory',
  component: InventoryPage,
});

const wisdomRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/wisdom',
  component: WisdomPage,
});

const appointmentsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/appointments',
  component: AppointmentsPage,
});

const mechanicDetailRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/team/$mechanicId',
  component: MechanicDetailPage,
});

const slotsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/slots',
  component: WorkshopSlotsPage,
});

const carsInShopRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/in-shop',
  component: guarded('/in-shop', CarsInShopPage),
});

const reportsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/reports',
  component: guarded('/reports', ReportsPage),
});

const toolReportsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/tool-reports',
  component: ToolReportsPage,
});

const supportRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/support',
  component: SupportPage,
});

const supportTicketRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/support/$ticketId',
  component: SupportTicketPage,
});

const settingsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/settings',
  component: SettingsPage,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  layoutRoute.addChildren([
    dashboardRoute,
    serviceOrdersRoute,
    serviceOrderNewRoute,
    serviceOrderDetailRoute,
    teamRoute,
    mechanicDetailRoute,
    financeRoute,
    inventoryRoute,
    appointmentsRoute,
    wisdomRoute,
    slotsRoute,
    carsInShopRoute,
    reportsRoute,
    toolReportsRoute,
    supportRoute,
    supportTicketRoute,
    settingsRoute,
  ]),
]);

export const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
